import React, { useEffect, useState, Fragment } from 'react';
export function AlaCarteQuote({
  priceType,
  onAddToCart,
  pricingData,
  onPendingItemsChange,
  showPricing
}) {
  // Initialize quantities with all items set to 0
  const [quantities, setQuantities] = useState(() => {
    const initialQuantities = {};
    pricingData.forEach(item => {
      initialQuantities[item.id] = 0;
    });
    return initialQuantities;
  });
  const [runningTotal, setRunningTotal] = useState(0);
  // Calculate running total whenever quantities change
  useEffect(() => {
    let total = 0;
    let hasItems = false;
    for (const [itemId, quantity] of Object.entries(quantities)) {
      const item = pricingData.find(item => item.id === itemId);
      if (item && quantity > 0) {
        total += item[priceType] * quantity;
        hasItems = true;
      }
    }
    setRunningTotal(total);
    onPendingItemsChange(hasItems);
  }, [quantities, priceType, pricingData, onPendingItemsChange]);
  const handleQuantityChange = (itemId, quantity) => {
    setQuantities({
      ...quantities,
      [itemId]: parseInt(quantity) || 0
    });
  };
  const handleAddToQuote = () => {
    const cartItems = {};
    for (const [itemId, quantity] of Object.entries(quantities)) {
      if (quantity > 0) {
        cartItems[itemId] = quantity;
      }
    }
    if (Object.keys(cartItems).length === 0) {
      alert('Please add at least one item to your  quote');
      return;
    }
    onAddToCart(cartItems);
    // Reset quantities to 0 after adding to quote
    const resetQuantities = {};
    pricingData.forEach(item => {
      resetQuantities[item.id] = 0;
    });
    setQuantities(resetQuantities);
  };
  // Helper function to check if an item is a transmitter
  const isTransmitter = itemId => {
    return itemId === 'chestTransmitter' || itemId === 'headTransmitter';
  };
  // Helper function to determine item category
  const getItemCategory = item => {
    if (item.category) return item.category;
    if (item.id === 'software') return 'software';
    if (item.id === 'receiver') return 'receiver';
    if (item.id === 'chestTransmitter') return 'transmitter';
    if (item.id === 'headTransmitter') return 'transmitter';
    if (item.id === 'judgeBox') return 'judgeBox';
    if (item.id.includes('headGuard')) return 'headGuard';
    if (item.id.includes('chestGuard')) return 'chestGuard';
    return 'other';
  };
  // Helper function to determine if an item is red or blue
  const getItemColor = item => {
    if (item.id.includes('Red')) return 'red';
    if (item.id.includes('Blue')) return 'blue';
    return null;
  };
  // Group items by category
  const groupedItems = pricingData.reduce((acc, item) => {
    const category = getItemCategory(item);
    if (!acc[category]) acc[category] = [];
    acc[category].push(item);
    return acc;
  }, {});
  // Order categories
  const categoryOrder = ['software', 'receiver', 'transmitter', 'judgeBox', 'headGuard', 'chestGuard', 'other'];
  // Get category display name
  const getCategoryDisplayName = category => {
    switch (category) {
      case 'software':
        return 'Software';
      case 'receiver':
        return 'Receivers';
      case 'transmitter':
        return 'Transmitters';
      case 'judgeBox':
        return 'Judge Boxes';
      case 'headGuard':
        return 'Head Guards';
      case 'chestGuard':
        return 'Chest Guards';
      default:
        return 'Other Items';
    }
  };
  // Count how many items have been added
  const itemCount = Object.values(quantities).reduce((count, quantity) => count + (quantity > 0 ? 1 : 0), 0);
  return <div className="mb-8 relative" data-id="element-12">
      <h2 className="text-2xl font-bold text-blue-900 mb-4" data-id="element-13">Ala Carte Items</h2>
      <div className="overflow-x-auto" data-id="element-14">
        <table className="min-w-full bg-white border" data-id="element-15">
          <thead data-id="element-16">
            <tr className="bg-gray-100" data-id="element-17">
              <th className="py-2 px-4 border text-left" data-id="element-18">Item</th>
              <th className="py-2 px-4 border text-center" data-id="element-19">Quantity</th>
            </tr>
          </thead>
          <tbody data-id="element-20">
            {categoryOrder.map(category => {
            if (!groupedItems[category] || groupedItems[category].length === 0) return null;
            return <Fragment key={category} data-id="element-21">
                  <tr className="bg-gray-200" data-id="element-22">
                    <td colSpan={2} className="py-2 px-4 border font-semibold text-gray-700" data-id="element-23">
                      {getCategoryDisplayName(category)}
                    </td>
                  </tr>
                  {groupedItems[category].map(item => {
                const itemColor = getItemColor(item);
                const rowColorClass = itemColor === 'red' ? 'border-l-4 border-l-red-500' : itemColor === 'blue' ? 'border-l-4 border-l-blue-500' : '';
                return <tr key={item.id} className={rowColorClass} data-id="element-24">
                        <td className="py-2 px-4 border" data-id="element-25">
                          <div className="font-medium flex items-center" data-id="element-26">
                            {itemColor && <div className={`w-3 h-3 rounded-full mr-2 ${itemColor === 'red' ? 'bg-red-500' : 'bg-blue-500'}`} data-id="element-27" />}
                            {item.name}
                          </div>
                          {item.description && <div className="text-sm text-gray-600 ml-5" data-id="element-28">
                              {item.description}
                            </div>}
                          {isTransmitter(item.id) && <div className="text-sm text-amber-600 mt-1 font-medium ml-5" data-id="element-29">
                              Note: At least two transmitters are required to
                              operate a system
                            </div>}
                        </td>
                        <td className="py-2 px-4 border" data-id="element-30">
                          <input type="number" min="0" value={quantities[item.id]} onChange={e => handleQuantityChange(item.id, e.target.value)} className="border rounded-md px-3 py-1 w-20 mx-auto block" data-id="element-31" />
                        </td>
                      </tr>;
              })}
                </Fragment>;
          })}
          </tbody>
        </table>
      </div>
      {/* Floating item count */}
      {itemCount > 0 && <div className="fixed bottom-6 right-6 bg-blue-600 text-white py-3 px-5 rounded-lg shadow-lg z-50 flex flex-col items-end" data-id="element-32">
          <div className="text-lg font-bold" data-id="element-33">
            {itemCount} {itemCount === 1 ? 'item' : 'items'} selected
          </div>
        </div>}
      <div className="mt-6" data-id="element-34">
        <button className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700" onClick={handleAddToQuote} data-id="element-35">
          Add to Quote
        </button>
      </div>
    </div>;
}